<script lang="ts">
	import { onDestroy } from 'svelte';
	import { format } from 'svelte-i18n';
	import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
	import type { GetStatsResponseHistoryData } from 'src/types/requests/charts/get_stats_period';
	import { devices_config } from '@devices/index';
	import { devices } from 'src/stores';
	import EmWidgetState from '../EmWidgetState.svelte';
	import EmWidgetShell from '../EmWidgetShell.svelte';
	import EmHeatmapHoverSurface from '../EmHeatmapHoverSurface.svelte';
	import {
		CalendarHeatmapChart,
		Icon,
		Loader,
		PlainTable,
		SelectV2,
		Stack,
		Tab,
		Tabs,
		Text,
	} from '@Shelly/shelly-design-system';
	import { fetchCompleteEnergyHeatmapHistory } from '../helpers/fetch_energy_heatmap_history';
	import {
		buildEnergyHeatmapCacheKey,
		buildHeatmapRefreshControls,
		energyHeatmapCacheCoversRange,
		mergeEnergyHeatmapHistory,
		readEnergyHeatmapCache,
		writeEnergyHeatmapCache,
	} from '../helpers/energy_heatmap_cache';
	import { fetchDeviceEnergyStatistics } from '../helpers/fetch_device_energy_statistics';
	import { fetchEnergyStatistics } from '../helpers/fetch_energy_statistics';
	import { fetchWholeHouseEnergyHistory } from '../helpers/fetch_pv_energy_history';
	import {
		buildDemoHeatmapHistory,
		buildEnergyHeatmapModel,
		heatmapMetricColorSchema,
		parseEnergyHistoryDate,
		type EnergyHeatmapMetric,
		type EnergyHeatmapScope,
	} from '../helpers/energy_heatmap_helpers';
	import { buildHeatmapYearControls } from '../helpers/heatmap_period_helpers';
	import { getEnergyChartEmptyMessage } from '../helpers/energy_flow_helpers';
	import {
		ENERGY_ADD_DEVICE_HREF,
		ENERGY_PV_SETTINGS_HREF,
		hasPowerMeterDevices,
	} from '../helpers/energy_widget_state_helpers';
	import { getPVSetupDevices } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';
	import { getActivePvSetupType } from '../helpers/pv_flow_helpers';
	import { isEnergyMonitoringDemoMode } from '../helpers/energy_data_mode';
	import { energyMonitoringDataMode } from '../helpers/store';
	import { currentUnitId } from 'src/stores/account/home';

	export let embedded = false;
	export let fluidWidth = false;
	export let staticPreview = false;
	export let scope: EnergyHeatmapScope = 'all';
	export let roomId: number | undefined;
	export let deviceId: string | undefined;
	export let metric: EnergyHeatmapMetric = 'consumption';
	export let showMetricToggle = false;
	export let controls: ChartControlsDataInterface | undefined;
	export let showPeriodTotal = true;
	export let widgetId = '';
	export let widgetTitle = '';
	export let expandedOnly = false;

	let activeMetric: EnergyHeatmapMetric = metric;
	let loading = true;
	let loadError = false;
	let history: GetStatsResponseHistoryData[] = [];
	let loadToken = 0;
	let activeMonthKey = '';

	const centerCurrentDate = (node: HTMLElement) => {
		let firstFrame = 0;
		let secondFrame = 0;
		let delayedCenter: ReturnType<typeof setTimeout>;

		const center = () => {
			if (node.clientWidth > 520 || node.scrollWidth <= node.clientWidth) return;
			const todayCell = node.querySelector<HTMLElement>('[data-calendar-heatmap-today]');
			if (!todayCell) return;

			const scrollRect = node.getBoundingClientRect();
			const todayRect = todayCell.getBoundingClientRect();
			const targetScrollLeft = Math.max(
				0,
				Math.min(
					node.scrollWidth - node.clientWidth,
					node.scrollLeft +
						todayRect.left -
						scrollRect.left +
						todayRect.width / 2 -
						node.clientWidth / 2,
				),
			);
			node.scrollTo({ left: targetScrollLeft, behavior: 'auto' });
		};

		const schedule = () => {
			cancelAnimationFrame(firstFrame);
			cancelAnimationFrame(secondFrame);
			clearTimeout(delayedCenter);
			firstFrame = requestAnimationFrame(() => {
				secondFrame = requestAnimationFrame(center);
			});
			delayedCenter = setTimeout(center, 250);
		};

		const resizeObserver = new ResizeObserver(schedule);
		resizeObserver.observe(node);
		schedule();

		return {
			destroy() {
				resizeObserver.disconnect();
				cancelAnimationFrame(firstFrame);
				cancelAnimationFrame(secondFrame);
				clearTimeout(delayedCenter);
			},
		};
	};

	$: if (!showMetricToggle) {
		activeMetric = metric;
	}

	$: effectiveControls = controls ?? buildHeatmapYearControls();

	$: metricOptions = [
		{
			value: 'consumption' as EnergyHeatmapMetric,
			title: $format('chart_legend_consumption', { default: 'Consumption' }),
		},
		{
			value: 'generation' as EnergyHeatmapMetric,
			title: $format('energy_monitoring_flow_generation', { default: 'Generation' }),
		},
	];

	$: selectedMetricOption =
		metricOptions.find((option) => option.value === activeMetric) ?? metricOptions[0];

	const loadData = async (
		nextScope: EnergyHeatmapScope,
		nextRoomId: number | undefined,
		nextDeviceId: string | undefined,
		nextControls: ChartControlsDataInterface,
		unitId: string,
	) => {
		const token = ++loadToken;
		const scopeKey =
			nextScope === 'device'
				? `device:${nextDeviceId ?? ''}`
				: nextScope === 'room'
					? `room:${nextRoomId ?? ''}`
					: 'all';
		const cacheKey = buildEnergyHeatmapCacheKey(
			unitId,
			scopeKey,
			'year',
			String(nextControls.time.from.getFullYear()),
		);
		const cachedHistory = readEnergyHeatmapCache(cacheKey);
		const hasCompleteCache = energyHeatmapCacheCoversRange(cacheKey, nextControls);
		if (cachedHistory.length) history = cachedHistory;
		loading = cachedHistory.length === 0;
		loadError = false;

		try {
			if (staticPreview || isEnergyMonitoringDemoMode()) {
				history = buildDemoHeatmapHistory();
				return;
			}

			let nextHistory: GetStatsResponseHistoryData[];
			const refreshControls = buildHeatmapRefreshControls(nextControls, hasCompleteCache);
			if (nextScope === 'device' && nextDeviceId) {
				const device = $devices[nextDeviceId];
				if (!device || !devices_config[device.type]?.isPM) {
					history = [];
					return;
				}
				nextHistory = await fetchCompleteEnergyHeatmapHistory(refreshControls, (range) =>
					fetchDeviceEnergyStatistics(device, range, 'year', {
						mergeLastHour: true,
						requireChartData: false,
					}),
				);
			} else if (nextScope === 'room' && nextRoomId != null) {
				nextHistory = await fetchCompleteEnergyHeatmapHistory(refreshControls, (range) =>
					fetchEnergyStatistics(range, 'year', nextRoomId, {
						mergeLastHour: true,
						requireChartData: false,
					}),
				);
			} else {
				nextHistory = await fetchCompleteEnergyHeatmapHistory(refreshControls, (range) =>
					fetchWholeHouseEnergyHistory(range, 'year'),
				);
			}
			if (token !== loadToken) return;
			history = mergeEnergyHeatmapHistory(cachedHistory, nextHistory).filter((point) => {
				const timestamp = parseEnergyHistoryDate(point.datetime).getTime();
				return (
					timestamp >= nextControls.time.from.getTime() &&
					timestamp <= nextControls.time.to.getTime()
				);
			});
			writeEnergyHeatmapCache(cacheKey, history, nextControls);
		} catch {
			if (token === loadToken) {
				if (!cachedHistory.length) history = [];
				loadError = cachedHistory.length === 0;
			}
		} finally {
			if (token === loadToken) loading = false;
		}
	};

	$: {
		$currentUnitId;
		$energyMonitoringDataMode;
		void loadData(scope, roomId, deviceId, effectiveControls, $currentUnitId);
	}

	onDestroy(() => {
		loadToken += 1;
	});

	$: model = buildEnergyHeatmapModel(history, activeMetric);
	$: calendarYear = model.calendarData.start.getFullYear();
	$: currentMonthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
	$: monthTabs = Array.from({ length: 12 }, (_, month) => {
		const date = new Date(calendarYear, month, 1);
		const monthKey = `${calendarYear}-${String(month + 1).padStart(2, '0')}`;
		return {
			monthKey,
			label: date.toLocaleDateString(undefined, { month: 'short' }),
			disabled: date.getTime() > currentMonthStart.getTime(),
		};
	});
	$: enabledMonthTabs = monthTabs.filter((tab) => !tab.disabled);
	$: if (!activeMonthKey || monthTabs.find((tab) => tab.monthKey === activeMonthKey)?.disabled) {
		activeMonthKey =
			[...model.monthlySummaries].reverse().find((summary) => summary.activeDayCount > 0)
				?.monthKey ??
			enabledMonthTabs.at(-1)?.monthKey ??
			'';
	}
	$: activeMonthlySummary = model.monthlySummaries.find(
		(summary) => summary.monthKey === activeMonthKey,
	);
	$: hasData = model.calendarData.data.some((point) => point.value > 0);
	$: pvSetupDevices = getPVSetupDevices(getActivePvSetupType());
	$: hasCompletePvSetup =
		pvSetupDevices.gridDevices.length > 0 && pvSetupDevices.solarDevices.length > 0;
	$: emptyMessage = getEnergyChartEmptyMessage($devices, (key, fallback) =>
		$format(key, { default: fallback }),
	);
	$: errorMessage = $format('energy_monitoring_heatmap_load_error', {
		default: 'Energy history could not be loaded. Please try again.',
	});
	$: colorSchema = heatmapMetricColorSchema(activeMetric);
	$: heatmapColorScheme =
		activeMetric === 'generation'
			? [
					'color-mix(in srgb, var(--color-chart-generated-energy) 14%, transparent)',
					'color-mix(in srgb, var(--color-chart-generated-energy) 26%, transparent)',
					'color-mix(in srgb, var(--color-chart-generated-energy) 40%, transparent)',
					'color-mix(in srgb, var(--color-chart-generated-energy) 55%, transparent)',
					'color-mix(in srgb, var(--color-chart-generated-energy) 70%, transparent)',
					'color-mix(in srgb, var(--color-chart-generated-energy) 85%, transparent)',
					'var(--color-chart-generated-energy)',
				]
			: [
					'color-mix(in srgb, var(--color-chart-consumed-energy) 14%, transparent)',
					'color-mix(in srgb, var(--color-chart-consumed-energy) 26%, transparent)',
					'color-mix(in srgb, var(--color-chart-consumed-energy) 40%, transparent)',
					'color-mix(in srgb, var(--color-chart-consumed-energy) 55%, transparent)',
					'color-mix(in srgb, var(--color-chart-consumed-energy) 70%, transparent)',
					'color-mix(in srgb, var(--color-chart-consumed-energy) 85%, transparent)',
					'var(--color-chart-consumed-energy)',
				];
	$: metricLabel =
		activeMetric === 'generation'
			? $format('energy_monitoring_flow_generation', { default: 'Generation' })
			: $format('chart_legend_consumption', { default: 'Consumption' });
	$: resolvedWidgetTitle =
		widgetTitle || $format('dash_energy_heatmap_title', { default: 'Yearly Heatmap' });
	$: metricSelectId = widgetId ? `${widgetId}-metric` : 'energy-heatmap-metric';

	const getMetricValue = (detail: unknown): EnergyHeatmapMetric => {
		if (typeof detail === 'string') {
			return detail === 'generation' ? 'generation' : 'consumption';
		}
		if (typeof detail === 'object' && detail !== null && 'value' in detail) {
			const value = (detail as { value: unknown }).value;
			return value === 'generation' ? 'generation' : 'consumption';
		}
		return 'consumption';
	};

	const refresh = () => loadData(scope, roomId, deviceId, effectiveControls, $currentUnitId);

	const handleMetricChange = (event: CustomEvent) => {
		activeMetric = getMetricValue(event.detail);
	};

	const splitValueLabel = (label: string): { value: string; unit: string } => {
		const separatorIndex = label.lastIndexOf(' ');
		return separatorIndex > 0
			? { value: label.slice(0, separatorIndex), unit: label.slice(separatorIndex + 1) }
			: { value: label, unit: '' };
	};

	$: summaryItems = [
		{
			id: 'low',
			label: $format('dash_energy_heatmap_daily_low', { default: 'Daily low' }),
			value: splitValueLabel(model.minLabel),
			icon: 'fas fa-arrow-down',
			iconClass: 'em-heatmap-stat-icon-primary',
			detail: '',
		},
		{
			id: 'average',
			label: $format('dash_energy_heatmap_daily_average', { default: 'Daily average' }),
			value: splitValueLabel(model.averageLabel),
			icon: 'fas fa-chart-line',
			iconClass: 'em-heatmap-stat-icon-primary',
			detail: '',
		},
		{
			id: 'peak',
			label: $format('dash_energy_heatmap_daily_peak', { default: 'Daily peak' }),
			value: splitValueLabel(model.maxLabel),
			icon: 'fas fa-arrow-up',
			iconClass: 'em-heatmap-stat-icon-danger',
			detail: model.peakDateLabel,
		},
		{
			id: 'days',
			label: $format('dash_energy_heatmap_active_days', { default: 'Days with data' }),
			value: { value: String(model.activeDayCount), unit: '' },
			icon: 'fas fa-calendar-check',
			iconClass: 'em-heatmap-stat-icon-primary',
			detail: '',
		},
		...(showPeriodTotal
			? [
					{
						id: 'total',
						label: $format('dash_energy_heatmap_total_metric', {
							default: 'Total {metric}',
							values: { metric: metricLabel },
						}),
						value: splitValueLabel(model.totalLabel),
						icon: 'fas fa-bolt',
						iconClass: 'em-heatmap-stat-icon-primary',
						detail: $format('dash_energy_heatmap_tracked_days', {
							default: '{count} tracked days',
							values: { count: model.activeDayCount },
						}),
					},
				]
			: []),
	];
</script>

{#snippet metricSelect()}
	{#if showMetricToggle}
		<div class="em-heatmap-metric-select" data-heatmap-interactive>
			<SelectV2
				id={metricSelectId}
				icon={null}
				aria-label={$format('dash_energy_heatmap_metric', { default: 'Heatmap metric' })}
				size="m"
				options={metricOptions}
				value={selectedMetricOption}
				disabled={loading}
				on:change={handleMetricChange}
			/>
		</div>
	{/if}
{/snippet}

{#snippet heatmapBody(expanded = false)}
	{#if loading}
		<div class="em-heatmap-loading">
			<Loader size="m" />
		</div>
	{:else if loadError}
		<EmWidgetState kind="error" message={errorMessage} minHeight="160px" onRefresh={refresh} />
	{:else if activeMetric === 'generation' && !hasCompletePvSetup}
		<EmWidgetState
			kind="setup"
			message={$format('energy_monitoring_pv_setup_missing', {
				default: 'Set up grid and solar meters to see generated energy.',
			})}
			minHeight="160px"
			actionHref={ENERGY_PV_SETTINGS_HREF}
			actionLabel={$format('energy_monitoring_pv_configure', { default: 'Set up PV' })}
		/>
	{:else if !hasData}
		<EmWidgetState
			kind={hasPowerMeterDevices($devices) ? 'empty' : 'setup'}
			message={!hasPowerMeterDevices($devices)
				? $format('energy_monitoring_state_heatmap_meter_setup', {
						default: 'Add a power meter to see energy history.',
					})
				: emptyMessage}
			minHeight="160px"
			actionHref={!hasPowerMeterDevices($devices) ? ENERGY_ADD_DEVICE_HREF : ''}
			actionLabel={!hasPowerMeterDevices($devices)
				? $format('button_add_device', { default: 'Add device' })
				: ''}
		/>
	{:else}
		<div class="em-heatmap-content" class:em-heatmap-content-fluid={embedded || fluidWidth}>
			<div
				class="em-heatmap-calendar-scroll"
				use:centerCurrentDate
				role="region"
				data-dashboard-widget-interactive
				aria-label={$format('dash_energy_heatmap_calendar_label', {
					default: '{year} daily {metric} calendar heatmap',
					values: { year: model.yearLabel, metric: metricLabel },
				})}
			>
				<div class="em-heatmap-calendar-canvas">
					<EmHeatmapHoverSurface>
						{#key `${activeMetric}-${model.yearLabel}`}
							<CalendarHeatmapChart
								data={model.calendarData}
								{colorSchema}
								colorScheme={heatmapColorScheme}
								compactBreakpoint={expanded ? 10000 : 0}
								cellSize={expanded ? undefined : 10}
								aria-label={$format('dash_energy_heatmap_calendar_label', {
									default: '{year} daily {metric} calendar heatmap',
									values: { year: model.yearLabel, metric: metricLabel },
								})}
							/>
						{/key}
					</EmHeatmapHoverSurface>
				</div>
			</div>

			<div class="em-heatmap-meta">
				<Stack gap="xs" class="em-heatmap-legend-item">
					<span class="em-heatmap-legend-swatches" aria-hidden="true">
						{#each heatmapColorScheme as color}
							<span class="em-heatmap-legend-swatch" style={`background:${color}`}></span>
						{/each}
					</span>
					<Text styleType="body-small">{metricLabel}</Text>
				</Stack>
				{#if expanded}
					<Text styleType="body-small" class="text-color-muted em-heatmap-legend-hint">
						{$format('dash_energy_heatmap_intensity_hint', {
							default: 'Color intensity runs from the lowest to the highest day.',
						})}
					</Text>
				{/if}
			</div>

			<div
				class="em-heatmap-summary"
				class:em-heatmap-summary-with-total={showPeriodTotal}
				class:em-heatmap-summary-expanded={expanded}
			>
				{#each summaryItems as item (item.id)}
					{#snippet summaryItemContent()}
						<div class="em-heatmap-summary-heading">
							<div class="em-heatmap-summary-label">
								<Text styleType="body-small" class="text-color-muted">
									{item.label}
								</Text>
								{#if expanded && item.detail && item.id !== 'total'}
									<Text styleType="miniature" class="text-color-muted"
										>· {item.detail}</Text
									>
								{/if}
							</div>
							{#if expanded}
								<Icon icon={item.icon} size="m" class={item.iconClass} />
							{/if}
						</div>
						<div class="em-heatmap-summary-value">
							<Text styleType="h1" class="text-600">{item.value.value}</Text>
							{#if item.value.unit}
								<Text styleType="body">{item.value.unit}</Text>
							{/if}
						</div>
						{#if item.id === 'total' && item.detail}
							<Text styleType="miniature" class="text-color-muted">{item.detail}</Text>
						{/if}
					{/snippet}
					{#if expanded}
						<div
							class="em-heatmap-summary-item em-heatmap-summary-item-expanded em-heatmap-summary-item-{item.id}"
							style={`grid-area:${item.id}`}
						>
							{@render summaryItemContent()}
						</div>
					{:else}
						<div class="em-heatmap-summary-item em-heatmap-summary-item-{item.id}">
							{@render summaryItemContent()}
						</div>
					{/if}
				{/each}
			</div>

			{#if expanded && model.monthlySummaries.length}
				<div class="em-heatmap-monthly">
					<Text styleType="body" class="text-600">
						{$format('dash_energy_heatmap_monthly_summary', {
							default: 'Monthly daily summary',
						})}
					</Text>
					<Tabs useNewLayout location={activeMonthKey}>
						{#each monthTabs as month (month.monthKey)}
							<Tab
								useNewLayout
								active={month.monthKey === activeMonthKey}
								disabled={month.disabled}
								on:click={() => {
									if (!month.disabled) activeMonthKey = month.monthKey;
								}}
							>
								{month.label}
							</Tab>
						{/each}
					</Tabs>
					{#if activeMonthlySummary}
						<div class="em-heatmap-month-table-desktop">
							<PlainTable type="compact" class="em-heatmap-month-table">
								<thead>
									<tr>
										<th class="text-left"
											>{$format('option_month', { default: 'Month' })}</th
										>
										<th
											>{$format('dash_energy_heatmap_daily_low', {
												default: 'Daily low',
											})}</th
										>
										<th>
											{$format('dash_energy_heatmap_daily_average', {
												default: 'Daily average',
											})}
										</th>
										<th
											>{$format('dash_energy_heatmap_daily_peak', {
												default: 'Daily peak',
											})}</th
										>
										<th
											>{$format('dash_energy_heatmap_active_days', {
												default: 'Days with data',
											})}</th
										>
									</tr>
								</thead>
								<tbody>
									<tr>
										<th scope="row">{activeMonthlySummary.monthLabel}</th>
										<td>{activeMonthlySummary.minLabel}</td>
										<td>{activeMonthlySummary.averageLabel}</td>
										<td>{activeMonthlySummary.maxLabel}</td>
										<td>{activeMonthlySummary.activeDayCount}</td>
									</tr>
								</tbody>
							</PlainTable>
						</div>
						<dl class="em-heatmap-month-summary-mobile">
							<div>
								<dt>{$format('option_month', { default: 'Month' })}</dt>
								<dd>{activeMonthlySummary.monthLabel}</dd>
							</div>
							<div>
								<dt>
									{$format('dash_energy_heatmap_daily_low', {
										default: 'Daily low',
									})}
								</dt>
								<dd>{activeMonthlySummary.minLabel}</dd>
							</div>
							<div>
								<dt>
									{$format('dash_energy_heatmap_daily_average', {
										default: 'Daily average',
									})}
								</dt>
								<dd>{activeMonthlySummary.averageLabel}</dd>
							</div>
							<div>
								<dt>
									{$format('dash_energy_heatmap_daily_peak', {
										default: 'Daily peak',
									})}
								</dt>
								<dd>{activeMonthlySummary.maxLabel}</dd>
							</div>
							<div>
								<dt>
									{$format('dash_energy_heatmap_active_days', {
										default: 'Days with data',
									})}
								</dt>
								<dd>{activeMonthlySummary.activeDayCount}</dd>
							</div>
						</dl>
					{/if}
				</div>
			{/if}
		</div>
	{/if}
{/snippet}

{#if expandedOnly}
	<div class="em-heatmap-modal-content">
		{@render heatmapBody(true)}
	</div>
{:else if embedded}
	<section class="energy-widget-heatmap em-heatmap-card">
		<div class="em-heatmap-heading">
			<div class="em-heatmap-heading-text">
				<Text styleType="h2" class="text-600">{resolvedWidgetTitle}</Text>
			</div>
			<div class="em-heatmap-heading-actions">
				{@render metricSelect()}
			</div>
		</div>
		{@render heatmapBody(false)}
	</section>
{:else}
	<div class="em-heatmap-widget-shell">
		<EmWidgetShell widgetClass="energy-widget-heatmap" title={resolvedWidgetTitle}>
			<svelte:fragment slot="actions">
				{@render metricSelect()}
			</svelte:fragment>
			{@render heatmapBody(true)}
		</EmWidgetShell>
	</div>
{/if}

<style lang="scss">
	.em-heatmap-card,
	.em-heatmap-widget-shell :global(.energy-widget-heatmap.container) {
		width: 100%;
		min-width: 0;
		overflow: hidden;
		container-type: inline-size;
		container-name: energy-heatmap;
	}

	.em-heatmap-widget-shell {
		display: contents;
	}

	.em-heatmap-card [data-heatmap-interactive] {
		cursor: auto;
	}

	.em-heatmap-card :global(.calendar-heatmap-year-grid .calendar-heatmap-cell::after),
	.em-heatmap-widget-shell :global(.calendar-heatmap-year-grid .calendar-heatmap-cell::after) {
		border-width: 0 !important;
	}

	.em-heatmap-heading {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: var(--spacing-s);
		margin-bottom: var(--spacing-m);
		min-width: 0;
	}

	.em-heatmap-heading-text {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
		min-width: 0;
	}

	.em-heatmap-heading-actions {
		display: inline-flex;
		flex: 0 0 auto;
		align-items: center;
		gap: var(--spacing-xs);
		margin-left: auto;
	}

	.em-heatmap-content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
		min-width: 0;
	}

	.em-heatmap-content-fluid {
		width: 100%;
	}

	.em-heatmap-calendar-scroll {
		width: 100%;
		min-width: 0;
		overflow-x: auto;
		overflow-y: hidden;
		padding-bottom: var(--spacing-xxs);
		overscroll-behavior-inline: contain;
		scrollbar-width: thin;
		scrollbar-color: var(--color-4) transparent;
	}

	.em-heatmap-calendar-canvas {
		width: max(100%, 560px);
		min-width: 560px;
	}

	.em-heatmap-loading {
		display: flex;
		flex-direction: column;
		min-width: 0;
		min-height: 0;
		flex: 1;
		gap: var(--spacing-xs);
	}

	.em-heatmap-loading {
		align-items: center;
		justify-content: center;
		min-height: 120px;
	}

	.em-heatmap-metric-select {
		min-width: 148px;
		align-self: flex-end;
	}

	.em-heatmap-meta {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--spacing-s) var(--spacing-l);
		min-width: 0;
	}

	.em-heatmap-meta :global(.em-heatmap-legend-item.stack) {
		align-items: center;
		min-width: 0;
	}

	.em-heatmap-legend-swatches {
		display: inline-flex;
		gap: var(--spacing-xxs);
	}

	.em-heatmap-legend-swatch {
		width: 12px;
		height: 12px;
		border-radius: var(--radius-small);
	}

	.em-heatmap-meta :global(.em-heatmap-legend-hint) {
		margin-left: auto;
	}

	.em-heatmap-summary {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 0;
		min-width: 0;
		border-top: 1px solid rgba(255, 255, 255, 0.12);
		border-bottom: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-heatmap-summary-with-total {
		grid-template-columns: repeat(3, minmax(0, 1fr));
		grid-template-areas:
			'low total average'
			'peak total days';
	}

	.em-heatmap-summary-with-total .em-heatmap-summary-item-low {
		grid-area: low;
	}

	.em-heatmap-summary-with-total .em-heatmap-summary-item-average {
		grid-area: average;
	}

	.em-heatmap-summary-with-total .em-heatmap-summary-item-peak {
		grid-area: peak;
	}

	.em-heatmap-summary-with-total .em-heatmap-summary-item-days {
		grid-area: days;
	}

	.em-heatmap-summary-with-total .em-heatmap-summary-item-total {
		grid-area: total;
	}

	.em-heatmap-summary-item {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-xs);
		min-height: calc(var(--spacing-xl) + var(--spacing-m));
		padding: var(--spacing-xs);
		min-width: 0;
		text-align: center;
	}

	.em-heatmap-summary:not(.em-heatmap-summary-with-total) .em-heatmap-summary-item:nth-child(even) {
		border-left: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-heatmap-summary:not(.em-heatmap-summary-with-total) .em-heatmap-summary-item:nth-child(n + 3),
	.em-heatmap-summary-with-total:not(.em-heatmap-summary-expanded) .em-heatmap-summary-item-peak,
	.em-heatmap-summary-with-total:not(.em-heatmap-summary-expanded) .em-heatmap-summary-item-days {
		border-top: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-heatmap-summary-with-total:not(.em-heatmap-summary-expanded) .em-heatmap-summary-item-total {
		border-right: 1px solid rgba(255, 255, 255, 0.12);
		border-left: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-heatmap-summary-expanded {
		gap: var(--spacing-s-plus);
		border: 0;
	}

	.em-heatmap-summary-expanded:not(.em-heatmap-summary-with-total) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
		grid-template-areas:
			'low average'
			'peak days';
	}

	.em-heatmap-summary-expanded .em-heatmap-summary-item {
		align-items: stretch;
		justify-content: space-between;
		gap: var(--spacing-m);
		min-height: 96px;
		padding: var(--spacing-m);
		text-align: left;
	}

	.em-heatmap-summary-expanded .em-heatmap-summary-item {
		border-top: 0;
		border-left: 0;
	}

	.em-heatmap-summary-expanded .em-heatmap-summary-item-expanded,
	.em-heatmap-monthly {
		border: 1px solid rgba(255, 255, 255, 0.12);
		border-radius: 16px;
		background: color-mix(in srgb, var(--color-1) 38%, transparent);
	}

	.em-heatmap-summary-expanded .em-heatmap-summary-item + .em-heatmap-summary-item {
		border-left: 0;
	}

	.em-heatmap-summary-heading,
	.em-heatmap-summary-label,
	.em-heatmap-summary-value {
		display: flex;
		align-items: center;
		min-width: 0;
	}

	.em-heatmap-summary-heading {
		justify-content: center;
		gap: var(--spacing-s);
	}

	.em-heatmap-summary-label,
	.em-heatmap-summary-value {
		justify-content: center;
		gap: var(--spacing-xs);
	}

	.em-heatmap-summary-expanded .em-heatmap-summary-heading {
		justify-content: space-between;
	}

	.em-heatmap-summary-expanded .em-heatmap-summary-label,
	.em-heatmap-summary-expanded .em-heatmap-summary-value {
		justify-content: flex-start;
	}

	.em-heatmap-summary :global(.em-heatmap-stat-icon-primary) {
		color: var(--color-primary);
	}

	.em-heatmap-summary :global(.em-heatmap-stat-icon-danger) {
		color: var(--color-danger);
	}

	.em-heatmap-monthly {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
		min-width: 0;
		padding: var(--spacing-s);
	}

	.em-heatmap-month-table-desktop {
		min-width: 0;
	}

	.em-heatmap-month-table-desktop :global(.em-heatmap-month-table) {
		width: 100%;
		min-width: 0;
	}

	.em-heatmap-month-table-desktop :global(.em-heatmap-month-table thead) {
		background: color-mix(in srgb, var(--color-muted) 18%, transparent);
	}

	.em-heatmap-month-summary-mobile {
		display: none;
		margin: 0;
	}

	.em-heatmap-month-summary-mobile div {
		display: flex;
		justify-content: space-between;
		gap: var(--spacing-m);
		padding: var(--spacing-s) 0;
		border-bottom: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-heatmap-month-summary-mobile div:last-child {
		border-bottom: 0;
	}

	.em-heatmap-month-summary-mobile dt,
	.em-heatmap-month-summary-mobile dd {
		margin: 0;
	}

	.em-heatmap-month-summary-mobile dt {
		color: var(--color-muted);
	}

	.em-heatmap-month-summary-mobile dd {
		font-weight: 600;
		text-align: right;
	}

	.em-heatmap-modal-content {
		min-width: 0;
	}

	@container energy-heatmap (max-width: 760px) {
		.em-heatmap-heading,
		.em-heatmap-widget-shell :global(.energy-widget-heatmap .energy-widget-heading) {
			flex-wrap: wrap;
		}

		.em-heatmap-meta {
			align-items: flex-start;
		}

		.em-heatmap-meta :global(.em-heatmap-legend-hint) {
			width: 100%;
			margin-left: 0;
		}

		.em-heatmap-month-table-desktop {
			display: none;
		}

		.em-heatmap-month-summary-mobile {
			display: block;
		}

		.em-heatmap-summary-with-total {
			grid-template-columns: repeat(2, minmax(0, 1fr));
			grid-template-areas:
				'low average'
				'total total'
				'peak days';
		}

		.em-heatmap-summary-with-total:not(.em-heatmap-summary-expanded) .em-heatmap-summary-item-total {
			border-top: 1px solid rgba(255, 255, 255, 0.12);
			border-bottom: 1px solid rgba(255, 255, 255, 0.12);
			border-right: 0;
			border-left: 0;
		}

		.em-heatmap-summary-label,
		.em-heatmap-summary-value {
			flex-wrap: wrap;
		}
	}
</style>
